/**
 * HTTP client for the WaaGo API.
 *
 * Conventions implemented here mirror implementation/PHASE-003:
 *  - RFC 9457 Problem Details for every error
 *  - Bearer access token held in memory only (never localStorage — an XSS
 *    there is an account takeover); refresh lives in an httpOnly cookie
 *  - Single-flight refresh so parallel 401s cause exactly one refresh
 *  - Idempotency-Key on every mutating money request
 */

export type Problem = {
  type: string
  title: string
  status: number
  detail?: string
  instance?: string
  traceId?: string
  errors?: { field: string; code: string }[]
}

export class ApiError extends Error {
  readonly status: number
  readonly problem: Problem
  constructor(problem: Problem) {
    super(problem.detail ?? problem.title)
    this.name = 'ApiError'
    this.status = problem.status
    this.problem = problem
  }
  /** Switch on `type`, never on `detail` — detail is localized prose. */
  is(type: string): boolean {
    return this.problem.type.endsWith(type)
  }
}

const BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '/v1'

/** The same base, for the few callers that fetch outside {@link request}. */
export const API_BASE = BASE

let accessToken: string | null = null
let refreshInFlight: Promise<boolean> | null = null

/**
 * The routes that cause a one-time code to be delivered.
 *
 * <p>Tracked so the development-only "Récupérer le code" button can tell a
 * freshly delivered code from the previous one: the server keeps only the last
 * code per destination, so on a resend — or a second sign-in with the same
 * account — the stale, already-consumed code is what comes back, and the screen
 * that fills it in refuses it.
 */
const ISSUES_A_CODE =
  /^\/(auth\/(otp\/request|register|login(?!\/phone)|resend|password-reset\/phone\/request|unlock\/request)|me\/phone\/request|me\/payment-methods\/wallets(\/resend)?$)/
let lastCodeRequestAt = 0

/** When a code was last asked for, in epoch milliseconds; 0 if never. */
export function lastCodeRequest(): number {
  return lastCodeRequestAt
}

export const tokenStore = {
  set(token: string | null) {
    accessToken = token
  },
  get(): string | null {
    return accessToken
  },
}

async function toProblem(res: Response): Promise<Problem> {
  try {
    const body = (await res.json()) as Partial<Problem>
    return {
      type: body.type ?? 'about:blank',
      title: body.title ?? res.statusText,
      status: body.status ?? res.status,
      ...(body.detail !== undefined ? { detail: body.detail } : {}),
      ...(body.traceId !== undefined ? { traceId: body.traceId } : {}),
      ...(body.errors !== undefined ? { errors: body.errors } : {}),
    }
  } catch {
    return { type: 'about:blank', title: res.statusText, status: res.status }
  }
}

async function refresh(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) {
        accessToken = null
        return false
      }
      const body = (await res.json()) as { accessToken: string }
      accessToken = body.accessToken
      return true
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

/**
 * Restores a session from the refresh cookie on a cold page load.
 *
 * <p>Shares the single-flight above rather than posting to `/auth/refresh`
 * directly, which is not a detail: refresh tokens rotate, and two rotations
 * with the same token look exactly like a stolen one, so the server revokes the
 * whole family and the user is signed out. React's development double-mount
 * alone was enough to trigger that.
 */
export async function restoreSession(): Promise<boolean> {
  return refresh()
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** Required by the API on every money-mutating endpoint. */
  idempotencyKey?: string
  /** Set false for the auth endpoints themselves. */
  auth?: boolean
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, idempotencyKey, auth = true } = opts

  // Before the request goes out, not after: the server records the code while
  // the response is still in flight, so a mark taken afterwards can land past
  // it and reject the very code this call asked for.
  if (ISSUES_A_CODE.test(path)) lastCodeRequestAt = Date.now()

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { 'Accept-Language': 'fr' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (auth && accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey
    return fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    })
  }

  let res = await send()

  if (res.status === 401 && auth) {
    const ok = await refresh()
    if (ok) res = await send()
  }

  if (!res.ok) throw new ApiError(await toProblem(res))
  if (isEmpty(res)) return undefined as T
  return (await res.json()) as T
}

/**
 * True when a successful response carries no body to parse.
 *
 * <p>Checking only for 204 was a real bug: `POST /auth/register` answers 202
 * with an empty body, `res.json()` threw on it, and a signup that had in fact
 * succeeded was reported to the user as a failure. Any 2xx without JSON is
 * empty as far as a caller is concerned.
 */
function isEmpty(res: Response): boolean {
  if (res.status === 204 || res.status === 205) return true
  if (res.headers.get('Content-Length') === '0') return true
  const type = res.headers.get('Content-Type')
  return type === null || !type.includes('json')
}

/** Cursor pagination envelope — see PHASE-003 conventions. */
export type Page<T> = { data: T[]; nextCursor: string | null; hasMore: boolean }

export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}
